import { useState } from "react"
import { Tooltip } from "react-tooltip"
import { CellContext } from "@tanstack/react-table"
import classNames from "classnames"
import styled, { css } from "styled-components"

import { ApiTypes, UITypes } from "types"
import { CellComponentProps } from "types/table/cellComponentProps"
import { ReactComponent as CwaIcon } from "assets/svg/cwa.svg"
import { ReactComponent as ThermostatIcon } from "assets/svg/thermostat.svg"

import { OrderItemsModal } from "pages/contractor/Orders/OrderItemsModal"

import styles from "../index.module.scss"

const Container = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  flex-wrap: wrap;
`

const ItemContainer = styled.div<{ isClickable: boolean }>`
  display: flex;
  align-items: center;
  height: 28px;
  border-radius: 24px;
  gap: 4px;
  padding: 2px 8px;
  background-color: #ffffff;

  ${({ isClickable }) =>
    isClickable &&
    css`
      cursor: pointer;
      &:hover {
        opacity: 0.7;
      }
    `}
`

const IconWrapper = styled.div<{ isClickable: boolean }>`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 24px;
  height: 24px;

  ${({ isClickable }) =>
    isClickable &&
    css`
      svg {
        path {
          fill: #2db2c8;
        }
      }
    `}
`

const Quantity = styled.span<{ isClickable: boolean }>`
  font-size: 14px;
  font-weight: 400;
  color: #0f172a;

  ${({ isClickable }) =>
    isClickable &&
    css`
      color: #2db2c8;
      text-decoration: underline;
    `}
`

interface OrdersQuantityProps {
  cell: CellContext<UITypes.TableBodyRow, UITypes.TableBodyCell>
  cellComponentProps?: CellComponentProps
}

const OrdersQuantity = ({ cell, cellComponentProps }: OrdersQuantityProps) => {
  const items = cell.row.original.rowSettings.item as ApiTypes.Model.OrderNewItem[]
  const isVendor = cellComponentProps?.isVendor
  const isWHManager = cellComponentProps?.isWHManager
  const [showOrderItemsModal, setShowOrderItemsModal] = useState(false)
  const orderItem = cell.row.original.rowSettings.item[0]
  const orderNumber = orderItem.id
  const showClickableQuantity = !!cell.row.parentId || cell.row.original.subRows?.length === 0
  const isExpanded = cell.row.getIsExpanded()
  if (!items || items.length === 0 || (isExpanded && items.length > 1)) {
    return null
  }

  const showDetails =
    ["packed", "shipping", "archived"].includes(orderItem.status) && orderItem.product_type === "thermostat"
  const isPackedStatus = orderItem.status === "packed"

  const groupedItems = items.reduce(
    (acc, item) => {
      const productType = item.product_type
      if (!acc[productType]) {
        acc[productType] = 0
      }
      acc[productType] += item.quantity
      return acc
    },
    {} as Record<ApiTypes.Model.OrderProductType, number>,
  )

  const itemsToDisplay = Object.entries(groupedItems).map(([productType, quantity]) => ({
    productType: productType as ApiTypes.Model.OrderProductType,
    quantity,
  }))

  const handleOrderQuantityClick = () => {
    setShowOrderItemsModal(true)
  }

  if (itemsToDisplay.length === 0) {
    return null
  }

  const isClickable = showDetails && showClickableQuantity
  const tooltipId = `order-thermostat-quantity-${orderNumber}`

  return (
    <Container>
      {itemsToDisplay
        .sort((a, b) => b.productType.localeCompare(a.productType))
        .map(({ productType, quantity }) => {
          const Icon = productType === "thermostat" ? ThermostatIcon : CwaIcon

          return (
            <>
              <ItemContainer
                key={productType}
                data-tooltip-id={isClickable ? tooltipId : undefined}
                onClick={isClickable ? handleOrderQuantityClick : undefined}
                isClickable={isClickable}
              >
                <IconWrapper isClickable={isClickable}>
                  <Icon />
                </IconWrapper>
                <Quantity isClickable={isClickable}>{quantity}</Quantity>
              </ItemContainer>
              {isClickable && (
                <Tooltip
                  id={tooltipId}
                  variant="dark"
                  delayShow={200}
                  classNameArrow={styles.tooltipArrow}
                  className={classNames(styles.tooltip, styles.noBoxShadow)}
                >
                  See the list of devices
                </Tooltip>
              )}
            </>
          )
        })}
      {showOrderItemsModal && (
        <OrderItemsModal
          orderNumber={orderNumber}
          isVendor={!!isVendor}
          isWHManager={!!isWHManager}
          isPackedStatus={isPackedStatus}
          quantity={orderItem.quantity}
          onClose={() => setShowOrderItemsModal(false)}
        />
      )}
    </Container>
  )
}

export default OrdersQuantity
